import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel
} from '@material-ui/core';
import { ChevronDoubleRight, ChevronDoubleLeft } from 'components/CustomSVG';
import { FontPicker } from 'components';
import { googleFontsApiKey } from 'configs';
import { updateStylePalette } from '../../../redux/actions/styles_palette';


const FontsConfigurations = ({onStepChange}) => {
  const dispatch = useDispatch();
  const [fontsSet, formFontsSet] = React.useState([]);

  const {stylePalette} = useSelector((state) => state.styles_palette);

  const googleFontsString = stylePalette.typography['theme_design/defined_fonts/google_fonts_string'];
  const heading1FontFamily = stylePalette.typography['theme_design/font/heading1_font_family']?.replace(/'/g, '');
  const heading2FontFamily = stylePalette.typography['theme_design/font/heading2_font_family']?.replace(/'/g, '');
  const heading3FontFamily = stylePalette.typography['theme_design/font/heading3_font_family']?.replace(/'/g, '');
  const heading4FontFamily = stylePalette.typography['theme_design/font/heading4_font_family']?.replace(/'/g, '');
  const heading5FontFamily = stylePalette.typography['theme_design/font/heading5_font_family']?.replace(/'/g, '');
  const heading6FontFamily = stylePalette.typography['theme_design/font/heading6_font_family']?.replace(/'/g, '');
  const paragraphFontFamily = stylePalette.typography['theme_design/font/paragraph_font_family']?.replace(/'/g, '');
  const buttonsFontFamily = stylePalette.buttons['theme_design/colors/button_font_family']?.replace(/'/g, '');
  const mobileMenuFontFamily = stylePalette.mobile['theme_design/m_header/sub_menu_font_family']?.replace(/'/g, '');
  const mainMenuFontFamily = stylePalette.menu['theme_design/nav/font_family']?.replace(/'/g, '');
  const subMenuFontFamily = stylePalette.menu['theme_design/nav/dropdown_font_family']?.replace(/'/g, '');
  const categoryPromoFontFamily = stylePalette.category_page['theme_design/category/category_promo_font_family']?.replace(/'/g, '');
  const productPromoFontFamily = stylePalette.product_page['theme_design/product_page/product_promo_font_family']?.replace(/'/g, '');
  const cartPageBannerFontFamily = stylePalette.cart_page['theme_design/main/login_promo_font_family']?.replace(/'/g, '');
  const bodyFontFamily = stylePalette.typography['theme_design/font/body_font_family']?.replace(/'/g, '');
  const heading1FontSize = stylePalette.typography['theme_design/font/heading1_font_size'];
  const heading2FontSize = stylePalette.typography['theme_design/font/heading2_font_size'];
  const heading3FontSize = stylePalette.typography['theme_design/font/heading3_font_size'];
  const heading4FontSize = stylePalette.typography['theme_design/font/heading4_font_size'];
  const heading5FontSize = stylePalette.typography['theme_design/font/heading5_font_size'];
  const heading6FontSize = stylePalette.typography['theme_design/font/heading6_font_size'];
  const bodyFontSize = stylePalette.typography['theme_design/font/body_font_size'];
  const bodyLineHeight = stylePalette.typography['theme_design/font/paragraph_line_height'];
  const categories = ['sans-serif', 'serif', 'display', 'handwriting'];

  useEffect(() => {
    formGoogleFontString(fontsSet);
  }, [fontsSet]);

  useEffect(() => {
    formFontsSet([
      ...new Set([
        heading1FontFamily,
        heading2FontFamily,
        heading3FontFamily,
        heading4FontFamily,
        heading5FontFamily,
        heading6FontFamily,
        paragraphFontFamily,
        buttonsFontFamily,
        mobileMenuFontFamily,
        mainMenuFontFamily,
        subMenuFontFamily,
        categoryPromoFontFamily,
        productPromoFontFamily,
        cartPageBannerFontFamily,
        bodyFontFamily
      ])
    ]);
  }, [
    heading1FontFamily,
    heading2FontFamily,
    heading3FontFamily,
    heading4FontFamily,
    heading5FontFamily,
    heading6FontFamily,
    paragraphFontFamily,
    buttonsFontFamily,
    mobileMenuFontFamily,
    mainMenuFontFamily,
    subMenuFontFamily,
    categoryPromoFontFamily,
    productPromoFontFamily,
    cartPageBannerFontFamily,
    bodyFontFamily
  ]);

  const formGoogleFontString = (fontsSet) => {
    const fonts = fontsSet.reduce((acc, font) => {
      if (font) {
        const fontFamily = font.replace(/ /g, '+');
        return [...acc, `family=${fontFamily}:wght@100;200;300;400;500;600;700;800;900`];
      }

      return acc;
    }, []);

    const fontsString = `https://fonts.googleapis.com/css2?${fonts.join('&')}`;

    dispatch(updateStylePalette({
      ...stylePalette,
      typography: {
        ...stylePalette.typography,
        'theme_design/defined_fonts/google_fonts_string': fontsString
      }
    }));

    return fontsString;
  };

  const handleChangeGoogleFontString = (newFontDetails) => {
    if (fontsSet.includes(newFontDetails.family)) {
      return googleFontsString;
    }

    const fontFamilyToAdd = newFontDetails.family.includes(' ')
      ? newFontDetails.family.replace(/ /g, '+')
      : newFontDetails.family;

    const variantsToAdd = newFontDetails.variants
      .filter((v) => !v.includes('italic'))
      .map((v) => v === 'regular' ? '400' : v).join(';');

    const newFontStringToAdd = `${fontFamilyToAdd}:wght@${variantsToAdd}`;

    const prevFontsString = googleFontsString.replace('https://fonts.googleapis.com/css2?', '');
    const prevFontsArr = prevFontsString.split('family=').reduce((fonts, fontParam) => {
      if (fontParam.length) {
        const [font] = fontParam.split(':wght');
        const fontFamily = font.includes('+') ? font.replace('+', ' ') : font;

        if (fontsSet.includes(fontFamily)) {
          return [...fonts, fontParam.replace('&', '')];
        }
      }

      return fonts;
    }, []);

    const newGoogleFontsString = `https://fonts.googleapis.com/css2?family=${[...prevFontsArr, newFontStringToAdd].join('&family=')}`;

    return newGoogleFontsString;
  };

  const handleSetHeadingsColorSchemaFontFamily = (font, fontDetails) => {
    const googleFontsString = handleChangeGoogleFontString(fontDetails);
    dispatch(updateStylePalette({
      ...stylePalette,
      typography: {
        ...stylePalette.typography,
        'theme_design/defined_fonts/google_fonts_string': googleFontsString,
        'theme_design/font/heading1_font_family': `'${font}'`,
        'theme_design/font/heading2_font_family': `'${font}'`,
        'theme_design/font/heading3_font_family': `'${font}'`,
        'theme_design/font/heading4_font_family': `'${font}'`,
        'theme_design/font/heading5_font_family': `'${font}'`,
        'theme_design/font/heading6_font_family': `'${font}'`
      }
    }));
  };

  const handleSetBodyColorSchemaFontFamily = (font, fontDetails) => {
    const googleFontsString = handleChangeGoogleFontString(fontDetails);
    dispatch(updateStylePalette({
      ...stylePalette,
      typography: {
        ...stylePalette.typography,
        'theme_design/defined_fonts/google_fonts_string': googleFontsString,
        'theme_design/font/body_font_family': `'${font}'`,
        'theme_design/font/paragraph_font_family': `'${font}'`
      },
      buttons: {
        ...stylePalette.buttons,
        'theme_design/colors/button_font_family': `'${font}'`
      },
      mobile: {
        ...stylePalette.mobile,
        'theme_design/m_header/sub_menu_font_family': `'${font}'`
      },
      menu: {
        ...stylePalette.menu,
        'theme_design/nav/font_family': `'${font}'`,
        'theme_design/nav/dropdown_font_family': `'${font}'`
      },
      category_page: {
        ...stylePalette.category_page,
        'theme_design/category/category_promo_font_family': `'${font}'`
      },
      product_page: {
        ...stylePalette.product_page,
        'theme_design/product_page/product_promo_font_family': `'${font}'`
      },
      cart_page: {
        ...stylePalette.cart_page,
        'theme_design/main/login_promo_font_family': `'${font}'`
      }
    }));
  };

  const handleSetBodyColorSchemaFontSize = (fontSize) => {
    dispatch(updateStylePalette({
      ...stylePalette,
      typography: {
        ...stylePalette.typography,
        'theme_design/font/body_font_size': fontSize,
        'theme_design/font/paragraph_font_size': fontSize
      }
    }));
  };


  return (
    <div className="p-8">
      <div className="flex flex-sm-column w-full">
        <div className="border-1-light-grey w-full p-8">
          <div className="apply-font-headings pb-10" style={{fontSize: `${heading1FontSize}px`}}>Heading 1 ({heading1FontSize}px)</div>
          <div className="apply-font-headings pb-10" style={{fontSize: `${heading2FontSize}px`}}>Heading 2 ({heading2FontSize}px)</div>
          <div className="apply-font-headings pb-10" style={{fontSize: `${heading3FontSize}px`}}>Heading 3 ({heading3FontSize}px)</div>
          <div className="apply-font-headings pb-10" style={{fontSize: `${heading4FontSize}px`}}>Heading 4 ({heading4FontSize}px)</div>
          <div className="apply-font-headings pb-10" style={{fontSize: `${heading5FontSize}px`}}>Heading 5 ({heading5FontSize}px)</div>
          <div className="apply-font-headings pb-10" style={{fontSize: `${heading6FontSize}px`}}>Heading 6 ({heading6FontSize}px)</div>
          <div className="apply-font-body mt-5" style={{fontSize: `${bodyFontSize}px`, lineHeight: `${bodyLineHeight}`}}>
            Lorem ipsum dolor sit amet, consectetur adipiscing elit.
            Mauris neque ipsum, vehicula in ipsum et, vulputate luctus
            nibh. Aliquam vestibulum leo vitae viverra ultrices. Praesent
            quam nisi, dignissim sed bibendum id, consectetur sit amet leo.
            Mauris sollicitudin odio tortor. Class aptent taciti sociosqu ad
            litora torquent per conubia nostra, per inceptos himenaeos.
            Integer sollicitudin dictum purus a consectetur.
          </div>
        </div>
        <div className="w-full ml-sm-100">
          <Typography variant="body1" className="text-16 mb-2">
            Select headings font:
          </Typography>
          <FontPicker
            apiKey={googleFontsApiKey}
            activeFontFamily={heading1FontFamily}
            pickerId="headings"
            scripts={['latin']}
            onChange={(nextFont, fontDetails) => {
              handleSetHeadingsColorSchemaFontFamily(nextFont, fontDetails);
            }}
            limit="100"
            sort="popularity"
            categories={categories}
          />
          <Typography variant="body1" className="text-16 mb-2 mt-5">
            Select basic font:
          </Typography>
          <FontPicker
            apiKey={googleFontsApiKey}
            activeFontFamily={bodyFontFamily}
            pickerId="body"
            scripts={['latin']}
            onChange={(nextFont, fontDetails) => {
              handleSetBodyColorSchemaFontFamily(nextFont, fontDetails);
            }}
            limit="100"
            sort="popularity"
            categories={categories}
          />
          <Typography variant="body1" className="text-16 mb-2 mt-5">
            Select base font size:
          </Typography>
          <RadioGroup
            aria-label="scheme"
            name="scheme"
            value={bodyFontSize}
            onChange={(event) => {
              const { value } = event.target;
              handleSetBodyColorSchemaFontSize(value);
            }}
            className="pl-4"
          >
            <FormControlLabel
              value="14"
              control={<Radio color="primary" size="small" />}
              className="flex w-full"
              label="Small (14px)"
            />
            <FormControlLabel
              value="16"
              control={<Radio color="primary" size="small" />}
              className="flex w-full"
              label="Medium (16px)"
            />
            <FormControlLabel
              value="18"
              control={<Radio color="primary" size="small" />}
              className="flex w-full"
              label="Large (18px)"
            />
          </RadioGroup>
        </div>
      </div>
      <div className="flex items-center justify-between mt-10">
        <Button
          variant="contained"
          onClick={() => onStepChange(0)}
          title="Next"
          className="px-6 bg-dark"
        >
          <ChevronDoubleLeft />
          <span className="ml-2 text-16">Back</span>
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => onStepChange(2)}
          title="Next"
          className="px-6"
        >
          <span className="mr-2 text-16">Next</span>
          <ChevronDoubleRight />
        </Button>
      </div>
    </div>
  );
};

export default FontsConfigurations;
